import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Create Supabase client with proper configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'x-application-name': 'utlysningslabbet'
    }
  }
});

// Connection state management
let isConnected = false;
let connectionAttempts = 0;
const maxAttempts = 5;

// Exponential backoff helper
const getBackoffDelay = (attempt: number) => Math.min(1000 * Math.pow(2, attempt), 10000);

// Test connection with exponential backoff
export const testConnection = async (forceCheck = false): Promise<boolean> => {
  // If already connected and not forcing a check, return true
  if (isConnected && !forceCheck) {
    return true;
  }

  try {
    // Simple connection test query
    const { count, error } = await supabase
      .from('waitlist')
      .select('*', { count: 'exact', head: true });

    if (error) {
      throw error;
    }

    console.log('✓ Supabase connection established');
    isConnected = true;
    connectionAttempts = 0;
    return true;
  } catch (error) {
    connectionAttempts++;
    console.warn(`⚠ Supabase connection attempt ${connectionAttempts} failed:`, error.message);
    
    if (connectionAttempts < maxAttempts) {
      const delay = getBackoffDelay(connectionAttempts);
      console.log(`Retrying in ${delay/1000} seconds...`);
      
      return new Promise((resolve) => {
        setTimeout(async () => {
          resolve(await testConnection(true));
        }, delay);
      });
    }
    
    console.error('❌ Failed to establish Supabase connection after', maxAttempts, 'attempts');
    isConnected = false;
    return false;
  }
};

// Initialize connection test
testConnection().catch(console.error);

// Export connection status checker
export const getConnectionStatus = () => ({
  isConnected,
  attempts: connectionAttempts
});

// Export connection reset function
export const resetConnection = () => {
  isConnected = false;
  connectionAttempts = 0;
  return testConnection(true);
};